/**
 * Jeopardy on the TV, and the host's control surface when TV control is on.
 * `kinds={['verdict']}` on purpose: a driving TV may show every host card, but a grading
 * list in front of the room is not wanted.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import {
  JoinQr,
  JudgmentTray,
  platform,
  Scoreboard,
  Timer,
  type GameUiProps,
  type SessionEvents,
} from '@gsf/kit';
import { defaultRoundName, type JeopardyPhase, type JeopardyVerdict } from '@gsf/games';
import { clueIsSpent, jeopardyView, type JeopardyView } from './view';
import { BoardGrid } from './BoardGrid';
import { BuzzList } from './BuzzList';
import { RoundTabs } from './RoundTabs';

export function JeopardyDisplay({ snapshot, send, submit, events, connected, notify }: GameUiProps) {
  const view = jeopardyView(snapshot);
  const tvControl = view.core.displayCanDrive;
  const phase = view.shared.phase;
  const clueMode = view.shared.current?.mode;
  const buzzStatus = useBuzzStatus(phase, events);

  const [pending, setPending] = useState<string | null>(null);

  const enableAndReveal = useCallback(async () => {
    if (pending === null) return;
    const clueId = pending;
    setPending(null);
    try {
      await platform.setDisplayCanDrive(view.core.code, true);
      // tv_reveal is committed before this lands, so core authorizes the display action.
      send('selectClue', { clueId, reveal: true });
    } catch {
      notify('Only the host can turn this on — use the host screen.');
    }
  }, [pending, view.core.code, send, notify]);

  // Esc dismisses the prompt. The flow-key handler below is bound only under control, so
  // this never double-fires with it.
  useEffect(() => {
    if (pending === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPending(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  // Flash the stage when someone buzzes in.
  const buzzedN = events.buzzed?.n;
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (buzzedN === undefined) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 500);
    return () => clearTimeout(t);
  }, [buzzedN]);

  // The host screen's flow keys, bound only under TV control. Y/N belong to `VerdictCard`.
  useEffect(() => {
    if (!tvControl) return;
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === ' ') {
        // preventDefault also stops a just-clicked button re-firing on Space.
        event.preventDefault();
        if (phase === 'selected') send('reveal');
        else if (phase === 'revealed' && clueMode === 'buzzer') send('openBuzzers');
      } else if (event.key === 'Escape') {
        if (phase !== 'board') send('returnToBoard');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tvControl, phase, clueMode, send]);

  // The host's "Display scores": a few seconds of scoreboard over whatever is on screen.
  const scoresShownN = events.scoresShown?.n;
  const [scoresPopup, setScoresPopup] = useState(false);
  useEffect(() => {
    if (scoresShownN === undefined) return;
    setScoresPopup(true);
    const t = setTimeout(() => setScoresPopup(false), SCORES_POPUP_MS);
    return () => clearTimeout(t);
  }, [scoresShownN]);

  const { core, shared } = view;
  const scoreboard = (
    <Scoreboard players={view.standings} activeParticipantId={shared.activeParticipantId} showPresence />
  );
  const sideOpen = shared.scoresDocked || shared.phase !== 'board';

  return (
    <div className="tv">
      <div className="tv-header">
        <h1 className="tv-title">{core.title}</h1>
        <span className="spacer" />
        {/* Small: the host needs to see at a glance whether this screen is driving, and a
            way out of a clue. */}
        {tvControl ? <TvControls view={view} send={send} /> : null}
        {!connected ? <span className="tag tag-bad">reconnecting…</span> : null}
      </div>

      <div className="tv-main">
        <div className="tv-stage-col">
          {/* Navigation under TV control, otherwise just the round label: buttons the server
              would refuse are worse than none. */}
          {shared.phase === 'board' ? (
            tvControl ? (
              <RoundTabs
                className="round-tabs-tv"
                rounds={shared.rounds}
                round={shared.round}
                onPick={(round) => send('setRound', { round })}
              />
            ) : (
              <span className="tv-round">
                {shared.rounds.find((r) => r.round === shared.round)?.name ??
                  defaultRoundName(shared.round)}
              </span>
            )
          ) : null}

          <div className={`tv-stage${flash ? ' buzz-flash' : ''}`} data-buzz={buzzStatus}>
            {/* Only under TV control (core refuses display actions otherwise) and only mid-clue. */}
            {tvControl && shared.phase !== 'board' ? (
              <button
                type="button"
                className="btn btn-sm tv-stage-back"
                title={
                  clueIsSpent(view)
                    ? 'Back to the board — this clue is spent (Esc)'
                    : 'Back out of this clue — the tile goes back on the board (Esc)'
                }
                onClick={(e) => act(e, () => send('returnToBoard'))}
              >
                ← Back
              </button>
            ) : null}
            <Stage
              view={view}
              // Under control a tile picks-and-reveals; otherwise a tile offers to turn
              // control on, so a dead click never reads as broken (#20). The display can't
              // tell if this viewer owns the session (that flag is host-only and the display
              // snapshot is room-wide), so the enable is simply tried — it works with the
              // owner's cookie and fails gracefully for a bare display token.
              onPick={
                tvControl
                  ? (clueId) => send('selectClue', { clueId, reveal: true })
                  : (clueId) => setPending(clueId)
              }
            />
          </div>
          {tvControl ? <TvActions view={view} send={send} submit={submit} /> : null}
        </div>

        {sideOpen ? (
          <div className="tv-side">
            {shared.scoresDocked ? (
              <div className="panel">
                <h2 className="panel-title">Scores</h2>
                {scoreboard}
              </div>
            ) : null}
            {shared.phase !== 'board' ? (
              <div className="panel">
                <h2 className="panel-title">Buzz order</h2>
                <BuzzList buzzes={view.buzzes} activeParticipantId={shared.activeParticipantId} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="tv-footer">
        <JoinQr code={core.code} />
        <span className="spacer" />
        <span className="faint">
          {view.standings.filter((p) => p.connected).length} of {view.standings.length} players
          connected
        </span>
      </div>

      {scoresPopup ? (
        <div className="modal-backdrop" onClick={() => setScoresPopup(false)}>
          <div className="modal" role="dialog" aria-label="Scores">
            <h2 className="panel-title" style={{ marginTop: 0 }}>
              Scores
            </h2>
            {scoreboard}
          </div>
        </div>
      ) : null}

      {pending !== null ? (
        <div className="modal-backdrop" onClick={() => setPending(null)}>
          <div
            className="modal"
            role="dialog"
            aria-label="Reveal from TV view"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="panel-title" style={{ marginTop: 0 }}>
              Do you want to reveal clues from the TV view?
            </p>
            <p className="faint" style={{ marginTop: 0 }}>
              This screen will drive the game from now on. You can turn it off from the host
              screen.
            </p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setPending(null)}>
                No
              </button>
              <button className="btn btn-primary" autoFocus onClick={() => void enableAndReveal()}>
                Yes
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** How long the host's "Display scores" holds the scoreboard on the TV. */
const SCORES_POPUP_MS = 8000;

/** How long the stage stays red after a wrong answer. */
const WRONG_FLASH_MS = 2000;

/**
 * The stage's side bars: gray locked, green open, yellow taken, plus a brief red on a
 * wrong ruling, overlaid on whatever the reducer moved to. Keyed on `judged.n`, a delivery
 * counter, so two wrong rulings in a row each flash.
 */
type BuzzStatus = 'off' | 'open' | 'held' | 'wrong';

function useBuzzStatus(phase: JeopardyPhase, events: SessionEvents): BuzzStatus {
  const [wrong, setWrong] = useState(false);
  const judged = events.judged;

  useEffect(() => {
    if (!judged) return;
    // A correct ruling right after a wrong one clears the red early — the
    // clue is over and a red bar over the answer would read as a verdict.
    const verdict = judged.payload?.verdict as JeopardyVerdict | undefined;
    if (verdict !== 'incorrect') {
      setWrong(false);
      return;
    }
    setWrong(true);
    const t = setTimeout(() => setWrong(false), WRONG_FLASH_MS);
    return () => clearTimeout(t);
  }, [judged]);

  // Leaving the clue drops the red immediately; nothing to be wrong about.
  useEffect(() => {
    if (phase === 'board' || phase === 'resolved') setWrong(false);
  }, [phase]);

  if (wrong) return 'wrong';
  if (phase === 'buzzers_open') return 'open';
  if (phase === 'answering') return 'held';
  return 'off';
}

/** Bounds for the fitted clue text, in px. */
const CLUE_MIN_PX = 16;
const CLUE_MAX_PX = 180;

/**
 * Fit the clue to its box by binary search on the real rendered height: line count
 * depends on the words, not the character count.
 */
function useFitText(text: string) {
  // A callback ref, not a plain one: the box mounts when the clue is revealed,
  // which is a phase change and not a text change, so the fit has to be driven
  // by the element appearing rather than by the deps of a render.
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  const fit = useCallback(() => {
    const el = textRef.current;
    if (!box || !el) return;
    const { clientWidth: w, clientHeight: h } = box;
    if (!w || !h) return;

    let lo = CLUE_MIN_PX;
    let hi = CLUE_MAX_PX;
    // Eight halvings of a 164px range lands within a pixel — well under what
    // anyone can see from a couch, and cheap enough to redo on every resize.
    for (let i = 0; i < 8; i += 1) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      if (el.scrollHeight <= h && el.scrollWidth <= w) lo = mid;
      else hi = mid;
    }
    el.style.fontSize = `${lo}px`;
  }, [box]);

  useLayoutEffect(() => {
    if (!box) return;
    fit();
    // The box is sized by its flex parent, never by the text inside it, so
    // re-fitting on its resize can't feed back into another resize.
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [box, text, fit]);

  return { boxRef: setBox, textRef };
}

/**
 * Clicking leaves the button focused, and a focused button eats the next
 * Space — so hand focus back to the page after every press.
 */
function act(event: MouseEvent<HTMLButtonElement>, fire: () => void) {
  event.currentTarget.blur();
  fire();
}

/**
 * The TV's header chrome under TV control: a visible way out of a clue, labelled with
 * its shortcut. Scoring stays on the host screen.
 */
function TvControls({ view, send }: { view: JeopardyView; send: GameUiProps['send'] }) {
  const phase = view.shared.phase;

  return (
    <span className="tv-controls">
      <span className="tag" title="This screen is driving the game">
        TV control
      </span>

      {phase === 'selected' ? (
        <button className="btn btn-sm" onClick={(e) => act(e, () => send('reveal'))}>
          Reveal (Space)
        </button>
      ) : null}

      {phase !== 'board' ? (
        <button className="btn btn-sm" onClick={(e) => act(e, () => send('returnToBoard'))}>
          {phase === 'resolved' ? 'Next clue → (Esc)' : 'Back to board (Esc)'}
        </button>
      ) : null}
    </span>
  );
}

/** The buttons that run a live clue, under the question where the host is looking. */
function TvActions({
  view,
  send,
  submit,
}: {
  view: JeopardyView;
  send: GameUiProps['send'];
  submit: GameUiProps['submit'];
}) {
  const { shared, requests } = view;
  const phase = shared.phase;

  const judging = requests.some((one) => one.spec.kind === 'verdict');
  const canUnlock = phase === 'revealed' && shared.current?.mode === 'buzzer';
  const canShowAnswer = phase !== 'board' && phase !== 'wager' && phase !== 'resolved';

  if (!judging && !canUnlock && !canShowAnswer) return null;

  return (
    <div className="tv-actions">
      <JudgmentTray
        requests={requests}
        onDecide={submit}
        nameOf={view.nameOf}
        kinds={['verdict']}
        size="lg"
      />

      {canUnlock ? (
        <button
          className="btn btn-primary tv-action"
          onClick={(e) => act(e, () => send('openBuzzers'))}
        >
          Unlock buzzers (Space)
        </button>
      ) : null}

      {canShowAnswer ? (
        <button
          className="btn tv-action"
          title="Nobody got it — show the room the answer and spend the clue"
          onClick={(e) => act(e, () => send('revealAnswer'))}
        >
          Nobody knows
        </button>
      ) : null}
    </div>
  );
}

/** What the room is looking at right now. */
function Stage({
  view,
  /** Set only under TV control: makes the grid's tiles pick-and-reveal. */
  onPick,
}: {
  view: JeopardyView;
  onPick?: (clueId: string) => void;
}) {
  const { core, shared } = view;
  const clue = shared.current;
  const activeName = view.nameOf(shared.activeParticipantId);
  const { boxRef, textRef } = useFitText(view.prompt ?? '');

  if (shared.phase === 'board' || !clue) {
    return (
      <BoardGrid
        board={shared.board}
        className="tv-board"
        onPick={onPick}
        pickTitle={onPick ? 'Click to reveal this clue' : undefined}
      />
    );
  }

  // The tile turning out to be a daily double is the reveal. The room sees who
  // it belongs to and that they're betting — but not the clue, not yet.
  if (shared.phase === 'wager') {
    const who = view.nameOf(shared.wager?.participantId ?? null);
    return (
      <div className="tv-clue">
        <div className="tv-clue-category">Wager · {clue.categoryName}</div>
        <div className="tv-clue-body">
          <div className="tv-status">{who ? `${who} is wagering…` : 'Waiting for a wager'}</div>
        </div>
      </div>
    );
  }

  if (shared.phase === 'selected') {
    return (
      <div className="tv-clue">
        <div className="tv-clue-category">
          {clue.categoryName} · {clue.points}
        </div>
        <div className="tv-clue-body">
          <div className="tv-clue-value">{clue.points}</div>
          <div className="tv-status">Get ready…</div>
        </div>
      </div>
    );
  }

  // The final round: everyone bets at once, secretly, before the clue is shown. The room
  // sees the category and how many are in — never the amounts, never the prompt yet.
  if (shared.phase === 'final_wager') {
    const placed = shared.finalWagers.filter((w) => w.placed).length;
    return (
      <div className="tv-clue">
        <div className="tv-clue-category">Final Round · {clue.categoryName}</div>
        <div className="tv-clue-body">
          <div className="tv-status">
            {placed} of {view.standings.length} have wagered…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tv-clue">
      <div className="tv-clue-category">
        {clue.categoryName} · {clue.points}
        {view.wagerAmount ? ` · wager ${view.wagerAmount}` : ''}
      </div>

      <div className="tv-clue-body">
        <div className="tv-clue-promptbox" ref={boxRef}>
          <p className="tv-clue-prompt" ref={textRef}>
            {view.prompt}
          </p>
        </div>

        {shared.phase === 'collecting' ? (
          <>
            {/* Untimed clues have no deadline, so `<Timer>` shows no clock at all
                rather than a zero that reads as "time's up". */}
            <Timer endsAt={core.deadlineAt} serverNow={core.serverNow} />
            <div className="tv-status">
              {view.answers.length} of {view.standings.length} answers in
            </div>
          </>
        ) : null}

        {shared.phase === 'grading' ? (
          <div className="tv-status">Answers in — judging…</div>
        ) : null}

        {shared.phase === 'revealed' ? (
          <div className="tv-status is-locked">
            {clue.mode === 'daily_double'
              ? `${activeName ?? 'Player'}, your answer…`
              : 'Buzzers locked'}
          </div>
        ) : null}

        {shared.phase === 'buzzers_open' ? <div className="tv-status is-open">Buzz in!</div> : null}

        {shared.phase === 'answering' && activeName ? (
          <div className="tv-status is-open">{activeName} buzzed in</div>
        ) : null}

        {shared.phase === 'resolved' ? (
          <div className="tv-clue-answer">{view.clueAnswer}</div>
        ) : null}
      </div>
    </div>
  );
}
